import React, { useState, useEffect } from "react";
import { useTheme } from "../../../ThemeContext";
import Button from "../../common/button/Button";
import { getUsecaseStructure } from "../../../utils/CustomerUiAPI";
import "./UseCaseSelection.css";

const UseCaseSelection = ({ isOpen, onClose, onConfirm, selectedBranch, useCases }) => {
  const { themeMode, currentTheme } = useTheme();
  const [usecaseOptions, setUsecaseOptions] = useState([]);
  const [selectedUsecase, setSelectedUsecase] = useState("");
  const [selectedSubUsecase, setSelectedSubUsecase] = useState("");
  // Track which usecases are expanded
  const [expandedUsecases, setExpandedUsecases] = useState([]);

  useEffect(() => {
    if (useCases && useCases.length > 0) {
      // Filter usecases for the selected branch
      const branchUseCases = useCases.filter(uc => uc.branch === selectedBranch);
      // Group by usecase
      const grouped = {};
      branchUseCases.forEach(uc => {
        if (!grouped[uc.usecase]) grouped[uc.usecase] = [];
        grouped[uc.usecase].push(uc.subUsecase);
      });
      // Transform to array for rendering
      const formattedUseCases = Object.entries(grouped).map(([usecase, subUsecases]) => ({
        usecase,
        subUsecases: subUsecases.map(sub => ({ subUsecase: sub }))
      }));
      setUsecaseOptions(formattedUseCases);
    } else {
      setUsecaseOptions([]);
    }
  }, [useCases, selectedBranch]);

  const handleUsecaseToggle = (usecase) => {
    // Check if this usecase has only one subusecase and it's "default"
    if (usecase.subUsecases.length === 1 && usecase.subUsecases[0].subUsecase === 'default') {
      // Automatically select both usecase and subusecase, but don't confirm yet
      setSelectedUsecase(usecase.usecase);
      setSelectedSubUsecase('default');
      return;
    }
    
    // Original logic for usecases with multiple subusecases
    setExpandedUsecases((prev) =>
      prev.includes(usecase.usecase)
        ? [] // Collapse if already expanded
        : [usecase.usecase] // Only expand the clicked usecase
    );
  };

  const handleSubUsecaseSelect = (usecase, subUsecase) => {
    setSelectedUsecase(usecase.usecase);
    setSelectedSubUsecase(subUsecase);
    // Ensure the usecase is expanded
    if (!expandedUsecases.includes(usecase.usecase)) {
      setExpandedUsecases((prev) => [...prev, usecase.usecase]);
    }
    // Ensure the usecase is expanded
    if (!expandedUsecases.includes(usecase.usecase)) {
      setExpandedUsecases((prev) => [...prev, usecase.usecase]);
    }
  };

  const handleContinue = () => {
    onConfirm(selectedUsecase, selectedSubUsecase);
  };

  const formatUsecaseName = (name) => {
    if (!name) return "";
    return name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (!isOpen) return null;

  return (
    <div className={`usecase-selection-container ${themeMode}`}>
      <div
        className="usecase-selection-content"
        style={{
          background: currentTheme.settingPage.popupBackground,
          color: currentTheme.settingPage.textColor,
        }}
      >
        <div className="usecase-selection-header">
          <h2>
            <span style={{ fontSize: "1.25rem" }}>Select Usecase</span>
          </h2>
          <button
            className="close-button"
            onClick={onClose}
            style={{ color: currentTheme.settingPage.textColor }}
          >
            {themeMode === "dark" ? (
              <img src="/icons/cancel-grey.svg" width={"20vh"} />
            ) : (
              <img src="/icons/cancel-orange.svg" width={"20vh"} />
            )}
          </button>
        </div>
        <div className="selection-info">
          <p>
            <strong>Branch:</strong> {formatUsecaseName(selectedBranch)}
          </p>
        </div>
        {!selectedUsecase && (
          <div style={{ fontSize: '0.95rem', color: '#ff4e3a', margin: '0.5rem 0 0.5rem 0', fontWeight: 500 }}>
            Please select any one use case to continue
          </div>
        )}
        {selectedUsecase && selectedSubUsecase && (
          <div style={{ fontSize: '0.95rem', color: '#ff4e3a', margin: '0.5rem 0 0.5rem 0', fontWeight: 500 }}>
            Selected Usecase: {formatUsecaseName(selectedUsecase)} {selectedSubUsecase !== 'default' ? `- ${formatUsecaseName(selectedSubUsecase)}` : ''}
          </div>
        )}
        <div className="usecase-list">
          {usecaseOptions.map((usecase, index) => {
            const isUsecaseSelected = selectedUsecase === usecase.usecase;
            const isSubUsecaseSelected = isUsecaseSelected && !!selectedSubUsecase;
            const isExpanded = expandedUsecases.includes(usecase.usecase);
            // New: highlight if this usecase is the parent of the selected subusecase
            const isParentOfSelected = selectedUsecase === usecase.usecase && !!selectedSubUsecase;
            return (
              <div key={index}>
                <div
                  className={`usecase-item ${isUsecaseSelected && !isSubUsecaseSelected ? "selected" : ""} ${isExpanded ? "expanded" : ""} ${isParentOfSelected ? "has-selected-subusecase" : ""}`}
                  onClick={() => handleUsecaseToggle(usecase)}
                  style={{
                    background:
                      isUsecaseSelected && !isSubUsecaseSelected
                        ? currentTheme.settingPage.button
                        : currentTheme.settingPage.themeBackground,
                    color: currentTheme.settingPage.textColor,
                    border: isExpanded ? "2px solid #ff4e3a" : undefined,
                    cursor: usecase.subUsecases.length === 1 && usecase.subUsecases[0].subUsecase === 'default' ? 'pointer' : 'default',
                  }}
                >
                  <div className="usecase-name">
                    {formatUsecaseName(usecase.usecase)}
                    {isParentOfSelected && <span className="selected-dot" />}
                  </div>
                  {/* Only show dropdown arrow if there are multiple subUsecases */}
                  {(usecase.subUsecases && usecase.subUsecases.length > 1) && (
                    <div className="subusecase-indicator">
                      <img
                        src={
                          themeMode === "dark"
                            ? "/icons/down.svg"
                            : "/icons/down-black.png"
                        }
                        className="call-auto-home-icon"
                        style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                      />
                    </div>
                  )}
                </div>
                {/* Only show subusecases if there are multiple options */}
                {isExpanded && usecase.subUsecases.length > 1 && (
                  <div className="subusecase-section">
                    <div className="subusecase-list" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                      {usecase.subUsecases.map((subUsecase, subIdx) => (
                        <div
                          key={subIdx}
                          className={`subusecase-item ${isUsecaseSelected && selectedSubUsecase === subUsecase.subUsecase ? "selected" : ""}`}
                          onClick={() => handleSubUsecaseSelect(usecase, subUsecase.subUsecase)}
                          style={{
                            background:
                              isUsecaseSelected && selectedSubUsecase === subUsecase.subUsecase
                                ? currentTheme.settingPage.button
                                : currentTheme.settingPage.themeBackground,
                            color: currentTheme.settingPage.textColor,
                          }}
                        >
                          {subUsecase.subUsecase === 'default' ? 'Default' : formatUsecaseName(subUsecase.subUsecase)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="usecase-selection-footer">
          <Button
            label="Continue"
            backgroundColor={currentTheme.settingPage.button}
            color="#ffffff"
            width="30%"
            borderRadius="5px"
            height="4vh"
            onClick={handleContinue}
            disabled={
              !selectedUsecase ||
              (usecaseOptions.find(u => u.usecase === selectedUsecase)?.subUsecases.length > 1 && !selectedSubUsecase)
            }
          />
        </div>
      </div>
    </div>
  );
};

export default UseCaseSelection;
